import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Modal from 'flarum/common/components/Modal';
import Tooltip from 'flarum/common/components/Tooltip';
import LinkButton from 'flarum/common/components/LinkButton';
import IPAddress from 'flarum/common/components/IPAddress';
import ItemList from 'flarum/common/utils/ItemList';
import classList from 'flarum/common/utils/classList';
import app from 'flarum/forum/app';

import MapModal from 'flarum/fof/geoip/components/MapModal';
import getIPData from 'flarum/fof/geoip/helpers/getIPData';

export default class ApplicationHistoryModal extends Modal {
    oninit(vnode) {
        super.oninit(vnode);

        this.user = this.attrs.user;
        this.application = this.attrs.application;
        this.offset = 0;
        this.limit = Math.min(10, Math.ceil(($(window).height() - 250) / 50) & ~1);
        this.histories = [];
        this.totalHistories = 0;
        this.isLoadingPage = false;
        this.sameTypeInRow = 0;

        this.queryList();
    }

    className() {
        return 'ApplicationHistoryModal Modal--large';
    }

    title() {
        return app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.label');
    }

    content() {
        const histories = this.histories;

        const columns = this.columns().toArray();

        return (
            <div className="Modal-body">
                <div className="Form">
                    <div className="ApplicationHistoryModal">
                        <Button className="Button Button--primary" icon="fas fa-sync" onclick={this.queryList.bind(this)}>
                            { app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.refresh_button') }
                        </Button>
                        <section
                            className={classList(['ApplicationHistoryModal-grid', this.isLoadingPage ? 'ApplicationHistoryModal-grid--loadingPage' : 'ApplicationHistoryModal-grid--loaded'])}
                            style={{ '--columns': columns.length }}
                            role="table"
                            // +1 to account for header
                            aria-rowcount={histories.length + 1}
                            aria-colcount={columns.length}
                            aria-live="polite"
                            aria-busy={this.isLoadingPage ? 'true' : 'false'}
                        >
                            {/* Render columns */}
                            {
                                columns.map((column, colIndex) => (
                                    <div className="ApplicationHistoryModal-grid-header" role="columnheader" aria-colindex={colIndex + 1} aria-rowindex={1}>
                                        {column.name}
                                    </div>
                                ))
                            }
                            {/* Render application data */}
                            {
                                histories.map((history, rowIndex) => {
                                    const isShaded = (rowIndex > 0) && (histories[rowIndex - 1].type() == histories[rowIndex].type());
                                    this.sameTypeInRow = isShaded ? (this.sameTypeInRow + 1) : 0;

                                    return columns.map((col, colIndex) => {
                                        const columnContent = col.content && col.content(history), columnTooltip = { tooltipVisible: col.tooltip?undefined:false, ...(col.tooltip && col.tooltip(history)) };
                                        return (
                                            <Tooltip { ...columnTooltip }>
                                                <div
                                                    className={classList(['ApplicationHistoryModal-grid-rowItem', (this.sameTypeInRow % 2 > 0) && 'ApplicationHistoryModal-grid-rowItem--shaded', 'ApplicationHistoryModal-grid-rowItem--' + history.type() ])}
                                                    data-history-id={history.id()}
                                                    data-column-name={col.itemName}
                                                    aria-colindex={colIndex + 1}
                                                    // +2 to account for 0-based index, and for the header row
                                                    aria-rowindex={rowIndex + 2}
                                                    role="cell"
                                                >
                                                    {columnContent}
                                                </div>
                                            </Tooltip>
                                        );
                                    });
                                })
                            }
                            {/* Loading spinner that shows when a new page is being loaded */}
                            {this.isLoadingPage && <LoadingIndicator size='medium' />}
                            {/* Empty histories label */}
                            {!this.totalHistories && <p className='ApplicationHistoryList-empty'>{ app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.not_found') }</p>}
                        </section>
                        <nav className="ApplicationHistoryModal-gridPagination">
                            <Button
                                disabled={this.offset === 0}
                                title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.pagination.first_button')}
                                onclick={() => { this.offset = 0; this.queryList(); } }
                                icon="fas fa-step-backward"
                                className="Button Button--icon ApplicationHistoryModal-firstPageBtn"
                            />
                            <Button
                                disabled={this.offset === 0}
                                title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.pagination.back_button')}
                                onclick={() => { this.offset = Math.max(this.offset - this.limit, 0); this.queryList(); } }
                                icon="fas fa-chevron-left"
                                className="Button Button--icon ApplicationHistoryModal-backBtn"
                            />
                            <span className="ApplicationHistoryModal-pageNumber">
                                {this.offset + 1}-{Math.max(this.offset + 1, Math.min(this.offset + this.limit, this.totalHistories))}
                            </span>
                            <Button
                                disabled={(this.offset + this.limit) >= this.totalHistories}
                                title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.pagination.next_button')}
                                onclick={() => { this.offset = Math.min(this.offset + this.limit, this.totalHistories); this.queryList(); } }
                                icon="fas fa-chevron-right"
                                className="Button Button--icon ApplicationHistoryModal-nextBtn"
                            />
                            <Button
                                disabled={(this.offset + this.limit) >= this.totalHistories}
                                title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.pagination.last_button')}
                                onclick={() => { this.offset = this.totalHistories - this.limit; this.queryList(); } }
                                icon="fas fa-step-forward"
                                className="Button Button--icon ApplicationHistoryModal-lastPageBtn"
                            />
                        </nav>
                    </div>
                </div>
            </div>
        );
    }

    columns() {
        const columns = new ItemList();

        columns.add(
            'createdTime',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.created'),
                content: (history) => {
                    const createdTime = history.createdAt();
                    return (
                        <span className="ApplicationHistoryList-createdTime" title={createdTime}>
                            { dayjs(createdTime).format('LLL') }
                        </span>
                    )
                }
            },
            80
        );

        if ('fof-geoip' in flarum.extensions) {
            columns.add(
                'ipAddress',
                {
                    name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.ip_address'),
                    content: (history) => {
                        let content = new ItemList();

                        const ip_info = history.ipInfo();
                        if (ip_info) {
                            const { description, threat, image } = getIPData(ip_info);
                            content.add('ip_country_flag', image);
                        }

                        content.add('ip_address',
                            <a onclick={() => app.modal.show(MapModal, { ipInfo: ip_info, ipAddr: history.ipAddress() }, true) }>
                                <u>{ history.ipAddress() }</u>
                            </a>
                        );

                        return <IPAddress ip={ history.ipAddress() } ipInfo={ history.ipInfo() } key={ history.ipAddress() } />;
                        //return <div className="ip-info">{content.toArray()}</div>;
                    }
                },
                70
            );
        } else {
            columns.add(
                'ipAddress',
                {
                    name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.ip_address'),
                    content: (history) => history.ipAddress()
                },
                70
            );
        }

        columns.add(
            'message',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.message'),
                content: (history) => history.message()
            },
            60
        );

        columns.add(
            'attachment',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.attachment'),
                content: (history) => (
                    <Button
                        className="Button Button--transparent ApplicationHistoryList-editModalBtn"
                        title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.open')}
                        icon='fas fa-paperclip'
                        onclick={() => {
                            const attachment = JSON.parse(history.include());
                            const parsed = Object.keys(attachment).map((key, rowIndex) => [
                                <div class='item-history-attachment' data-column-name={key} aria-colindex={1} aria-rowindex={rowIndex+1} role="cell" style="font-weight: 600; text-align: right;">
                                    {key+':'}
                                </div>,
                                <div class='item-history-attachment' data-column-name={attachment[key]} aria-colindex={2} aria-rowindex={rowIndex+1} role="cell" style="font-weight: 300; text-align: left;">
                                    {attachment[key]}
                                </div>
                            ]);

                            app.alerts.show(
                                <section role="table" aria-rowcount={parsed.length+1} aria-colcount={parsed.length*2} aria-live="polite" style="display: inline-grid; grid-template-columns: auto auto; column-gap: 4px;">
                                    <div data-column-name={app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.attachment')} aria-colindex={1} aria-rowindex={1} role="row" style="display: grid; grid-auto-rows: 25px; grid-column: span 2; font-weight: 700; text-transform: uppercase;">
                                        {app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.attachment')+':'}
                                    </div>
                                    {parsed.length ? parsed : <p style="grid-column: span 2;">{app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.empty')}</p> }
                                </section>
                            );
                        }}
                    >
                        {app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.grid.open')}
                    </Button>
                )
            },
            -80
        );

        return columns;
    }

    queryList() {
        this.sameTypeInRow = 0;
        this.isLoadingPage = true;
        app.store.find('applications/histories', { sort: '-created_at', filter: { user: this.user.id(), application: this.application.id() }, page: { offset: this.offset, limit: this.limit }}).then((histories) => {
            this.histories = histories;
            this.totalHistories = histories.payload.meta.modelStatistics['application.histories'].total;
            this.isLoadingPage = false;
            m.redraw();
        });
    }
}
